import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNotify } from '@/hooks/useNotify';

export interface CopyToClipboardOptions {
  successMessage?: string;
  errorMessage?: string;
}

/**
 * Copies a string to the clipboard and shows a success or error toast (#2671).
 */
export function useCopyToClipboard() {
  const notify = useNotify();
  const { t: tc } = useTranslation('common');
  const [copied, setCopied] = useState(false);
  const resetTimeoutRef = useRef<number | undefined>(undefined);

  useEffect(() => {
    return () => {
      if (resetTimeoutRef.current !== undefined) {
        window.clearTimeout(resetTimeoutRef.current);
      }
    };
  }, []);

  const copy = useCallback(
    async (value: string, options?: CopyToClipboardOptions): Promise<boolean> => {
      try {
        await navigator.clipboard.writeText(value);
        setCopied(true);
        notify.success(options?.successMessage ?? tc('copied'));
        if (resetTimeoutRef.current !== undefined) {
          window.clearTimeout(resetTimeoutRef.current);
        }
        resetTimeoutRef.current = window.setTimeout(() => {
          setCopied(false);
          resetTimeoutRef.current = undefined;
        }, 2000);
        return true;
      } catch {
        notify.error(tc('error'), options?.errorMessage ?? tc('copyFailed'));
        return false;
      }
    },
    [notify, tc],
  );

  return { copy, copied };
}
